import {
  addMonths,
  addYears,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  lastDayOfMonth,
  max,
  min,
} from 'date-fns';
import * as React from 'react';

import { defaultRanges } from './calendarPicker.constants';
import {
  CalendarPickerDateRange,
  CalendarPickerDefinedRange,
  CalendarPickerNavigationAction,
} from './calendarPicker.types';
import { parseOptionalDate } from './calendarPicker.utils';
import Menu from './components/CalendarPickerMenu';

type Marker = symbol;

export const MARKERS: { [key: string]: Marker } = {
  FIRST_MONTH: Symbol('firstMonth'),
  SECOND_MONTH: Symbol('secondMonth'),
};

const getValidatedMonths = (range: CalendarPickerDateRange, minDate: Date, maxDate: Date) => {
  const { startDate, endDate } = range;
  if (startDate && endDate) {
    const newStart = max([startDate, minDate]);
    const newEnd = min([endDate, maxDate]);

    return [newStart, isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd];
  } else {
    return [startDate, endDate];
  }
};

interface Props {
  open: boolean;
  initialDateRange?: CalendarPickerDateRange;
  definedRanges?: CalendarPickerDefinedRange[];
  minDate?: Date | string;
  maxDate?: Date | string;
  showDefinedRanges?: boolean;
  isRange?: boolean;
  onChange: (dateRange: CalendarPickerDateRange) => void;
}

export const CalendarPicker: React.FC<Props> = ({
  open,
  onChange,
  initialDateRange,
  minDate,
  maxDate,
  definedRanges = defaultRanges,
  showDefinedRanges = false,
  isRange = false,
}) => {
  const today = new Date();
  const minDateValid = parseOptionalDate(minDate, new Date(1900, 0, 1));
  const maxDateValid = parseOptionalDate(maxDate, lastDayOfMonth(addYears(today, 50)));
  const [intialFirstMonth, initialSecondMonth] = getValidatedMonths(
    initialDateRange || {},
    minDateValid,
    maxDateValid,
  );
  const [dateRange, setDateRange] = React.useState<CalendarPickerDateRange>({
    ...initialDateRange,
  });
  const [hoverDay, setHoverDay] = React.useState<Date>();
  const [firstMonth, setFirstMonth] = React.useState<Date>(
    intialFirstMonth || (isRange ? addMonths(today, -1) : today),
  );
  const [secondMonth, setSecondMonth] = React.useState<Date>(initialSecondMonth || today);
  const { startDate, endDate } = dateRange;

  // handlers
  const setFirstMonthValidated = (date: Date) => {
    if (isRange) {
      if (isBefore(date, secondMonth)) {
        setFirstMonth(date);
      }
    } else {
      setFirstMonth(date);
    }
  };

  const setSecondMonthValidated = (date: Date) => {
    if (isAfter(date, firstMonth)) {
      setSecondMonth(date);
    }
  };

  const setDateRangeValidated = (range: CalendarPickerDateRange) => {
    let { startDate: newStart, endDate: newEnd } = range;

    if (newStart && newEnd) {
      range.startDate = newStart = max([newStart, minDateValid]);
      range.endDate = newEnd = min([newEnd, maxDateValid]);
      setDateRange(range);
      onChange(range);
      setFirstMonth(newStart);
      setSecondMonth(isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd);
    }
  };

  const onDayClick = (day: Date) => {
    if (isRange) {
      if (startDate && !endDate && !isBefore(day, startDate)) {
        const newRange = { startDate, endDate: day };
        onChange(newRange);
        setDateRange(newRange);
      } else {
        setDateRange({ startDate: day, endDate: undefined });
      }
      setHoverDay(day);
    } else {
      const newRange = { startDate: day, endDate: day };
      setDateRange(newRange);
      onChange(newRange);
    }
  };

  const onMonthNavigate = (marker: Marker, action: CalendarPickerNavigationAction) => {
    if (isRange) {
      if (marker === MARKERS.FIRST_MONTH) {
        const firstNew = addMonths(firstMonth, action);

        if (isBefore(firstNew, secondMonth) && isAfter(firstNew, minDateValid)) {
          setFirstMonth(firstNew);
        } else if (!isAfter(firstNew, minDateValid)) {
          setFirstMonth(minDateValid);
        }
      } else {
        const secondNew = addMonths(secondMonth, action);

        if (isBefore(firstMonth, secondNew) && isBefore(secondNew, maxDateValid)) {
          setSecondMonth(secondNew);
        } else if (!isBefore(secondNew, maxDateValid)) {
          setSecondMonth(maxDateValid);
        }
      }
    } else {
      const firstNew = addMonths(firstMonth, action);

      if (isAfter(firstNew, minDateValid) && isBefore(firstNew, maxDateValid)) {
        setFirstMonth(firstNew);
      } else if (isBefore(firstNew, minDateValid)) {
        setFirstMonth(minDateValid);
      } else if (isAfter(firstNew, maxDateValid)) {
        setFirstMonth(maxDateValid);
      }
    }
  };

  const onDayHover = (date: Date) => {
    if (startDate && !endDate) {
      if (!hoverDay || !isSameDay(date, hoverDay)) {
        setHoverDay(date);
      }
    }
  };

  // helpers
  const inHoverRange = (day: Date) => {
    return (startDate &&
      !endDate &&
      hoverDay &&
      isAfter(hoverDay, startDate) &&
      isWithinInterval(day, { start: startDate, end: hoverDay })) as boolean;
  };

  const helpers = {
    inHoverRange,
  };

  const handlers = {
    onDayClick,
    onDayHover,
    onMonthNavigate,
  };

  return open ? (
    <Menu
      dateRange={dateRange}
      minDate={minDateValid}
      maxDate={maxDateValid}
      ranges={definedRanges}
      firstMonth={firstMonth}
      secondMonth={secondMonth}
      setFirstMonth={setFirstMonthValidated}
      setSecondMonth={setSecondMonthValidated}
      setDateRange={setDateRangeValidated}
      isRange={isRange}
      showDefinedRanges={showDefinedRanges}
      helpers={helpers}
      handlers={handlers}
    />
  ) : null;
};
